import React, { useEffect, useRef, useState, useCallback } from "react";
import { connect, useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Switch, FormControlLabel, Chip } from "@mui/material";
import { Add, Delete, OpenInFull, Close } from "@mui/icons-material";
import {
  create_faq_question,
  get_blog_spotlight_content,
  upload_cms_photo,
} from "../../reduxData/cms/cmsAction";
import apiClient from "../../api/apiClient";

interface SpotlightButton {
  label: string;
  url: string;
}

interface SpotlightCard {
  id: string;
  colWidth: number;
  colWidthMobile: number;
  label: string;
  title: string;
  description: string;
  image: string;
  linkType: "internal" | "external";
  internalPostId: string;
  internalPostSlug: string;
  externalUrl: string;
  buttons: SpotlightButton[];
  countdownLabel: string;
  countdownDate: string; // datetime-local value, e.g. "2026-07-10T23:00"
}

type ColOption = 3 | 4 | 6 | 12;
type MobileColOption = 6 | 12;

const MAX_CARDS = 4;
const MAX_BUTTONS = 3;

const COL_OPTIONS: { value: ColOption; label: string; fill: number }[] = [
  { value: 3, label: "Quarter", fill: 25 },
  { value: 4, label: "Third", fill: 33 },
  { value: 6, label: "Half", fill: 50 },
  { value: 12, label: "Full", fill: 100 },
];

const MOBILE_COL_OPTIONS: {
  value: MobileColOption;
  label: string;
  fill: number;
}[] = [
  { value: 6, label: "Half", fill: 50 },
  { value: 12, label: "Full", fill: 100 },
];

const POST_TYPE_LABELS: Record<string, string> = {
  ARTICLE: "Article",
  COLLECTION: "Collection",
  EVENT: "Event",
  DISCUSSION: "Discussion",
};
const POST_TYPE_COLORS: Record<string, string> = {
  ARTICLE: "#1976d2",
  COLLECTION: "#7b1fa2",
  EVENT: "#c62828",
  DISCUSSION: "#388e3c",
};

const makeCard = (): SpotlightCard => ({
  id: Math.random().toString(36).slice(2),
  colWidth: 6,
  colWidthMobile: 6,
  label: "",
  title: "",
  description: "",
  image: "",
  linkType: "external",
  internalPostId: "",
  internalPostSlug: "",
  externalUrl: "",
  buttons: [],
  countdownLabel: "",
  countdownDate: "",
});


interface TimeLeft {
  expired: boolean;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const getTimeLeft = (targetDate: string): TimeLeft => {
  const diff = new Date(targetDate).getTime() - Date.now();
  if (!targetDate || isNaN(diff) || diff <= 0) {
    return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
  }
  return {
    expired: false,
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
};

const CountdownBadge: React.FC<{ label: string; targetDate: string }> = ({
  label,
  targetDate,
}) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() =>
    getTimeLeft(targetDate),
  );

  useEffect(() => {
    setTimeLeft(getTimeLeft(targetDate));
    const id = setInterval(() => setTimeLeft(getTimeLeft(targetDate)), 1000);
    return () => clearInterval(id);
  }, [targetDate]);

  if (timeLeft.expired) return null;

  const pad = (n: number) => String(n).padStart(2, "0");

  return (
    <div
      style={{
        position: "absolute",
        top: 10,
        right: 10,
        zIndex: 2,
        background: "rgba(0,0,0,0.72)",
        border: "1px solid #FFD600",
        borderRadius: 8,
        padding: "3px 9px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        lineHeight: 1.15,
      }}
    >
      {label && (
        <span
          style={{
            fontSize: "0.58rem",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            fontWeight: 700,
            color: "#ddd",
          }}
        >
          {label}
        </span>
      )}
      <span
        style={{
          fontSize: "0.78rem",
          fontWeight: 800,
          color: "#FFD600",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {timeLeft.days > 0 && `${timeLeft.days}d `}
        {pad(timeLeft.hours)}:{pad(timeLeft.minutes)}:{pad(timeLeft.seconds)}
      </span>
    </div>
  );
};

const PreviewCard: React.FC<{ card: SpotlightCard; isMain: boolean }> = ({
  card,
  isMain,
}) => (
  <div
    style={{
      position: "relative",
      borderRadius: isMain ? 16 : 12,
      overflow: "hidden",
      height: "100%",
      minHeight: isMain ? 220 : 160,
      background: "#222",
    }}
  >
    {card.image ? (
      <img
        src={card.image}
        alt={card.title}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
    ) : (
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(135deg,#1a1a1a,#333)",
        }}
      />
    )}

    {card.countdownDate && (
      <CountdownBadge
        label={card.countdownLabel}
        targetDate={card.countdownDate}
      />
    )}

    <div
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        background:
          "linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.4) 60%, transparent 100%)",
        padding: isMain ? "1rem" : "0.6rem",
        borderRadius: `0 0 ${isMain ? 16 : 12}px ${isMain ? 16 : 12}px`,
      }}
    >
      {card.label && (
        <span
          style={{
            fontSize: "0.7rem",
            textTransform: "uppercase",
            color: "#FFD600",
            fontWeight: 700,
            letterSpacing: "0.5px",
            display: "block",
            marginBottom: 3,
          }}
        >
          {card.label}
        </span>
      )}
      {card.title && (
        <div
          style={{
            fontWeight: 800,
            fontSize: isMain ? "1rem" : "0.82rem",
            color: "#fff",
            lineHeight: 1.2,
            textShadow: "0 1px 3px rgba(0,0,0,0.7)",
          }}
        >
          {card.title}
        </div>
      )}
      {card.description && (
        <div
          style={{
            fontSize: "0.72rem",
            color: "#e0e0e0",
            marginTop: 2,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {card.description}
        </div>
      )}
      {card.buttons.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            marginTop: 6,
          }}
        >
          {card.buttons.map((btn, i) => (
            <span
              key={i}
              style={{
                color: "#FFD600",
                textDecoration: "underline",
                textUnderlineOffset: 3,
                fontWeight: 700,
                fontSize: "0.7rem",
                textTransform: "uppercase",
                letterSpacing: "0.3px",
                whiteSpace: "nowrap",
              }}
            >
              {btn.label || "Button"} →
            </span>
          ))}
        </div>
      )}
    </div>
  </div>
);

const SpotlightPreviewLayout: React.FC<{
  cards: SpotlightCard[];
  isMobileView: boolean;
}> = ({ cards, isMobileView }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(12, 1fr)",
      gap: 12,
      alignItems: "stretch",
    }}
  >
    {cards.map((card, i) => {
      const cols = isMobileView ? card.colWidthMobile : card.colWidth;
      return (
        <div key={card.id} style={{ gridColumn: `span ${cols}` }}>
          <PreviewCard card={card} isMain={i === 0} />
        </div>
      );
    })}
  </div>
);

const PreviewModal: React.FC<{
  cards: SpotlightCard[];
  onClose: () => void;
}> = ({ cards, onClose }) => {
  const [containerWidth, setContainerWidth] = useState(1280);

  const BREAKPOINTS = [
    { label: "Mobile", width: 375 },
    { label: "Tablet", width: 768 },
    { label: "Desktop", width: 1280 },
    { label: "Wide", width: 1440 },
  ];

  const isMobileView = containerWidth < 768;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.88)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          background: "#1a1a1a",
          padding: "12px 20px",
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexShrink: 0,
          borderBottom: "1px solid #333",
        }}
      >
        <span
          style={{
            color: "#fff",
            fontWeight: 700,
            fontSize: "0.9rem",
            marginRight: 8,
          }}
        >
          Preview
        </span>

        <div style={{ display: "flex", gap: 6 }}>
          {BREAKPOINTS.map((bp) => (
            <button
              key={bp.label}
              onClick={() => setContainerWidth(bp.width)}
              style={{
                padding: "4px 12px",
                borderRadius: 6,
                fontWeight: 700,
                fontSize: "0.75rem",
                cursor: "pointer",
                border:
                  containerWidth === bp.width
                    ? "2px solid #FFD600"
                    : "1px solid #555",
                background:
                  containerWidth === bp.width ? "#FFD600" : "transparent",
                color: containerWidth === bp.width ? "#000" : "#ccc",
              }}
            >
              {bp.label}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
          <input
            type="range"
            min={320}
            max={1440}
            value={containerWidth}
            onChange={(e) => setContainerWidth(Number(e.target.value))}
            style={{ flex: 1, accentColor: "#FFD600" }}
          />
          <span
            style={{
              color: "#aaa",
              fontSize: "0.78rem",
              width: 55,
              textAlign: "right",
            }}
          >
            {containerWidth}px
          </span>
        </div>

        <span
          style={{
            color: isMobileView ? "#FFD600" : "#aaa",
            fontSize: "0.75rem",
            fontWeight: 700,
            minWidth: 80,
          }}
        >
          {isMobileView ? "📱 Mobile" : "🖥 Desktop"}
        </span>

        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: "#fff",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Close />
        </button>
      </div>

      <div
        style={{
          flex: 1,
          overflow: "auto",
          display: "flex",
          justifyContent: "center",
          padding: "40px 20px",
          background: "#2a2a2a",
        }}
      >
        <div
          style={{
            width: containerWidth,
            background: "#f8f8f8",
            padding: "24px",
            borderRadius: 8,
            minHeight: 300,
            transition: "width 0.2s",
          }}
        >
          <div
            style={{
              marginBottom: 20,
              paddingBottom: 10,
              borderBottom: "2px solid #FFD600",
            }}
          >
            <h2 style={{ fontWeight: 900, fontSize: "1.2rem", margin: 0 }}>
              Featured
            </h2>
          </div>
          <SpotlightPreviewLayout cards={cards} isMobileView={isMobileView} />
        </div>
      </div>
    </div>
  );
};

const CardEditor: React.FC<{
  card: SpotlightCard;
  index: number;
  total: number;
  onUpdate: (id: string, updates: Partial<SpotlightCard>) => void;
  onRemove: (id: string) => void;
  onMove: (id: string, dir: -1 | 1) => void;
  isPermission: boolean;
}> = ({ card, index, total, onUpdate, onRemove, onMove, isPermission }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [postSearch, setPostSearch] = useState("");
  const [postResults, setPostResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const searchTimeout = useRef<any>(null);

  const u = (updates: Partial<SpotlightCard>) => onUpdate(card.id, updates);

  useEffect(() => {
    if (card.linkType !== "internal") return;
    clearTimeout(searchTimeout.current);
    if (!postSearch.trim()) {
      setPostResults([]);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await apiClient.get(
          `/community/posts?search=${encodeURIComponent(postSearch)}&isAdminPost=true&limit=8`,
        );
        setPostResults(res.data?.data || []);
      } catch {
        setPostResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(searchTimeout.current);
  }, [postSearch, card.linkType]);

  const selectPost = (post: any) => {
    u({
      internalPostId: post._id,
      internalPostSlug: post.slug || post._id,
      title: card.title || post.title || "",
      description:
        card.description ||
        post.excerpt ||
        post.content?.replace(/<[^>]*>/g, "").slice(0, 120) ||
        "",
      image: card.image || post.featured_image || post.images?.[0] || "",
    });
    setPostSearch("");
    setPostResults([]);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await upload_cms_photo(
        [file],
        localStorage.getItem("token"),
        dispatch,
      );
      if (result?.data?.photos?.[0]) u({ image: result.data.photos[0] });
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const addButton = () => {
    if (card.buttons.length < MAX_BUTTONS)
      u({ buttons: [...card.buttons, { label: "", url: "" }] });
  };
  const updateButton = (
    i: number,
    field: keyof SpotlightButton,
    value: string,
  ) => {
    u({
      buttons: card.buttons.map((b, idx) =>
        idx === i ? { ...b, [field]: value } : b,
      ),
    });
  };
  const removeButton = (i: number) =>
    u({ buttons: card.buttons.filter((_, idx) => idx !== i) });

  const ColPicker = ({
    value,
    options,
    onChange,
    label,
  }: {
    value: number;
    options: { value: number; label: string; fill: number }[];
    onChange: (v: number) => void;
    label: string;
  }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span
        style={{
          fontSize: "0.72rem",
          fontWeight: 600,
          color: "#666",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      <div style={{ display: "flex", gap: 4 }}>
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => onChange(opt.value)}
              title={`${opt.label} — ${opt.fill}% wide`}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 3,
                padding: "4px 8px 5px",
                borderRadius: 6,
                minWidth: 52,
                border: active ? "2px solid #000" : "1px solid #ccc",
                background: active ? "#FFD600" : "#fff",
                cursor: "pointer",
              }}
            >
              <span
                style={{
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  lineHeight: 1,
                  color: active ? "#000" : "#555",
                }}
              >
                {opt.label}
              </span>
              <div
                style={{
                  width: "100%",
                  height: 4,
                  background: active ? "rgba(0,0,0,0.15)" : "#eee",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${opt.fill}%`,
                    height: "100%",
                    background: active ? "#000" : "#bbb",
                    borderRadius: 2,
                  }}
                />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  const cardHref =
    card.linkType === "internal"
      ? card.internalPostSlug
        ? `…/articles/${card.internalPostSlug}`
        : ""
      : card.externalUrl;

  const hasCountdown = !!(card.countdownLabel || card.countdownDate);

  return (
    <div
      style={{
        border: "1.5px solid #e0e0e0",
        borderRadius: 10,
        marginBottom: 16,
        background: "#fff",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          background: "#f9f9f9",
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #eee",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontWeight: 700, fontSize: "0.9rem" }}>
            Card {index + 1}
          </span>
          <ColPicker
            value={card.colWidth}
            options={COL_OPTIONS}
            onChange={(v) => u({ colWidth: v as ColOption })}
            label="Desktop:"
          />
          <ColPicker
            value={card.colWidthMobile}
            options={MOBILE_COL_OPTIONS}
            onChange={(v) => u({ colWidthMobile: v as MobileColOption })}
            label="Mobile:"
          />
          {cardHref && (
            <span
              style={{
                fontSize: "0.7rem",
                color: "#888",
                maxWidth: 180,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              → {cardHref}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button
            onClick={() => onMove(card.id, -1)}
            disabled={index === 0}
            style={{
              background: "none",
              border: "1px solid #ddd",
              borderRadius: 4,
              padding: "2px 8px",
              cursor: index === 0 ? "default" : "pointer",
              opacity: index === 0 ? 0.3 : 1,
            }}
          >
            ←
          </button>
          <button
            onClick={() => onMove(card.id, 1)}
            disabled={index === total - 1}
            style={{
              background: "none",
              border: "1px solid #ddd",
              borderRadius: 4,
              padding: "2px 8px",
              cursor: index === total - 1 ? "default" : "pointer",
              opacity: index === total - 1 ? 0.3 : 1,
            }}
          >
            →
          </button>
          <button
            onClick={() => onRemove(card.id)}
            style={{
              background: "none",
              border: "1px solid #ffcdd2",
              borderRadius: 4,
              padding: "2px 8px",
              cursor: "pointer",
              color: "#c62828",
            }}
          >
            <Delete fontSize="small" />
          </button>
        </div>
      </div>

      <div style={{ padding: 16 }}>
        <div className="row g-3">
          <div className="col-md-7">
            <div className="mb-3">
              <label className="fw-600 small mb-1 d-block">
                Label{" "}
                <span style={{ color: "#aaa", fontWeight: 400 }}>
                  (shown above title in yellow — e.g. EVENT, BLOG)
                </span>
              </label>
              <input
                className="form-control form-control-sm"
                placeholder="e.g. EVENT, GIVEAWAY, NEXT UP"
                value={card.label}
                onChange={(e) => u({ label: e.target.value })}
                disabled={!isPermission}
              />
            </div>
            <div className="mb-3">
              <label className="fw-600 small mb-1 d-block">Title</label>
              <input
                className="form-control form-control-sm"
                placeholder="Card title"
                value={card.title}
                onChange={(e) => u({ title: e.target.value })}
                disabled={!isPermission}
              />
            </div>
            <div className="mb-3">
              <label className="fw-600 small mb-1 d-block">Description</label>
              <textarea
                className="form-control form-control-sm"
                rows={2}
                placeholder="Short description"
                value={card.description}
                onChange={(e) => u({ description: e.target.value })}
                disabled={!isPermission}
              />
            </div>
            <div className="mb-2">
              <label className="fw-600 small mb-1 d-block">Link</label>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                {(["internal", "external"] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => u({ linkType: t })}
                    style={{
                      padding: "4px 14px",
                      borderRadius: 6,
                      border:
                        card.linkType === t
                          ? "2px solid #000"
                          : "1px solid #ccc",
                      background: card.linkType === t ? "#FFD600" : "#fff",
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      cursor: "pointer",
                      textTransform: "capitalize",
                    }}
                  >
                    {t === "internal" ? "Admin Post" : "External URL"}
                  </button>
                ))}
              </div>
              {card.linkType === "external" ? (
                <input
                  className="form-control form-control-sm"
                  placeholder="https://..."
                  value={card.externalUrl}
                  onChange={(e) => u({ externalUrl: e.target.value })}
                  disabled={!isPermission}
                />
              ) : (
                <div>
                  {card.internalPostId && (
                    <div style={{ marginBottom: 8 }}>
                      <Chip
                        label={card.title || card.internalPostSlug}
                        size="small"
                        onDelete={() =>
                          u({ internalPostId: "", internalPostSlug: "" })
                        }
                        color="primary"
                      />
                    </div>
                  )}
                  <div style={{ position: "relative" }}>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Search admin posts…"
                      value={postSearch}
                      onChange={(e) => setPostSearch(e.target.value)}
                      disabled={!isPermission}
                    />
                    {(searching || postResults.length > 0) && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          background: "#fff",
                          border: "1px solid #ddd",
                          borderRadius: 6,
                          zIndex: 100,
                          maxHeight: 200,
                          overflowY: "auto",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        }}
                      >
                        {searching && (
                          <div
                            style={{
                              padding: "8px 12px",
                              fontSize: "0.8rem",
                              color: "#888",
                            }}
                          >
                            Searching…
                          </div>
                        )}
                        {postResults.map((post) => (
                          <div
                            key={post._id}
                            onClick={() => selectPost(post)}
                            style={{
                              padding: "8px 12px",
                              cursor: "pointer",
                              borderBottom: "1px solid #f0f0f0",
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = "#f9f9f9")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = "#fff")
                            }
                          >
                            {post.featured_image && (
                              <img
                                src={post.featured_image}
                                alt=""
                                style={{
                                  width: 32,
                                  height: 32,
                                  objectFit: "cover",
                                  borderRadius: 4,
                                }}
                              />
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontWeight: 600,
                                  fontSize: "0.8rem",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {post.title}
                              </div>
                              <div
                                style={{ fontSize: "0.7rem", color: "#888" }}
                              >
                                <span
                                  style={{
                                    background:
                                      POST_TYPE_COLORS[post.postType] || "#555",
                                    color: "#fff",
                                    padding: "1px 6px",
                                    borderRadius: 4,
                                    marginRight: 4,
                                    fontSize: "0.65rem",
                                  }}
                                >
                                  {POST_TYPE_LABELS[post.postType] ||
                                    post.postType}
                                </span>
                                {post.author?.user_name}
                              </div>
                            </div>
                          </div>
                        ))}
                        {!searching &&
                          postResults.length === 0 &&
                          postSearch && (
                            <div
                              style={{
                                padding: "8px 12px",
                                fontSize: "0.8rem",
                                color: "#888",
                              }}
                            >
                              No results
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-3">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 6,
                }}
              >
                <label className="fw-600 small mb-0">
                  Countdown{" "}
                  <span style={{ color: "#aaa", fontWeight: 400 }}>
                    (optional)
                  </span>
                </label>
                {hasCountdown && isPermission && (
                  <button
                    onClick={() =>
                      u({ countdownLabel: "", countdownDate: "" })
                    }
                    style={{
                      background: "none",
                      border: "1px solid #ffcdd2",
                      borderRadius: 4,
                      padding: "2px 8px",
                      fontSize: "0.72rem",
                      cursor: "pointer",
                      color: "#c62828",
                    }}
                  >
                    <Close fontSize="small" style={{ fontSize: 14 }} /> Clear
                  </button>
                )}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input
                  className="form-control form-control-sm"
                  placeholder='Label, e.g. "Starts in"'
                  value={card.countdownLabel}
                  onChange={(e) => u({ countdownLabel: e.target.value })}
                  style={{ flex: "1 1 140px" }}
                  disabled={!isPermission}
                />
                <input
                  type="datetime-local"
                  className="form-control form-control-sm"
                  value={card.countdownDate}
                  onChange={(e) => u({ countdownDate: e.target.value })}
                  style={{ flex: "1 1 180px" }}
                  disabled={!isPermission}
                />
              </div>
              <span style={{ fontSize: "0.7rem", color: "#aaa" }}>
                Leave blank to hide the badge. It disappears automatically
                once the date/time passes. Uses your browser's local
                timezone.
              </span>
            </div>

            <div className="mt-3">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 6,
                }}
              >
                <label className="fw-600 small mb-0">
                  CTA Links{" "}
                  <span style={{ color: "#aaa", fontWeight: 400 }}>
                    ({card.buttons.length}/{MAX_BUTTONS})
                  </span>
                </label>
                {card.buttons.length < MAX_BUTTONS && isPermission && (
                  <button
                    onClick={addButton}
                    style={{
                      background: "none",
                      border: "1px solid #ccc",
                      borderRadius: 4,
                      padding: "2px 8px",
                      fontSize: "0.75rem",
                      cursor: "pointer",
                    }}
                  >
                    <Add fontSize="small" /> Add
                  </button>
                )}
              </div>
              {card.buttons.map((btn, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    gap: 6,
                    marginBottom: 6,
                    alignItems: "center",
                  }}
                >
                  <input
                    className="form-control form-control-sm"
                    placeholder="Label"
                    value={btn.label}
                    onChange={(e) => updateButton(i, "label", e.target.value)}
                    style={{ width: 110, flexShrink: 0 }}
                    disabled={!isPermission}
                  />
                  <input
                    className="form-control form-control-sm"
                    placeholder="URL"
                    value={btn.url}
                    onChange={(e) => updateButton(i, "url", e.target.value)}
                    disabled={!isPermission}
                  />
                  <button
                    onClick={() => removeButton(i)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#c62828",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    <Delete fontSize="small" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="col-md-5">
            <label className="fw-600 small mb-1 d-block">Image</label>
            <div
              style={{
                width: "100%",
                aspectRatio: "16/9",
                background: "#f5f5f5",
                borderRadius: 8,
                overflow: "hidden",
                border: "1.5px dashed #ccc",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                marginBottom: 8,
              }}
            >
              {card.image ? (
                <>
                  <img
                    src={card.image}
                    alt="Card preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  {isPermission && (
                    <button
                      onClick={() => u({ image: "" })}
                      style={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                        background: "rgba(0,0,0,0.6)",
                        border: "none",
                        borderRadius: "50%",
                        width: 26,
                        height: 26,
                        color: "#fff",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Close fontSize="small" />
                    </button>
                  )}
                </>
              ) : (
                <span style={{ fontSize: "0.78rem", color: "#bbb" }}>
                  No image
                </span>
              )}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              className="d-none"
              accept="image/png,image/jpeg,image/webp,image/gif"
              onChange={handleImageUpload}
            />
            <button
              className="btn btn-sm w-100"
              style={{
                background: "#FFD600",
                fontWeight: 700,
                border: "2px solid #000",
              }}
              onClick={() => fileInputRef.current?.click()}
              disabled={!isPermission || uploading}
            >
              {uploading ? "Uploading…" : "Upload Image"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface SpotlightManagerProps {
  blogSpotlightContent: any[];
}

const SpotlightManager: React.FC<SpotlightManagerProps> = ({
  blogSpotlightContent,
}) => {
  const { user } = useSelector((state: any) => state.auth);
  const dispatch = useDispatch();
  const [cards, setCards] = useState<SpotlightCard[]>([makeCard(), makeCard()]);
  const [status, setStatus] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const isPermission =
    (user?.role === "staff" && user?.permission?.p_staff > 1) ||
    user?.role !== "staff";

  useEffect(() => {
    get_blog_spotlight_content(
      localStorage.getItem("token"),
      "blog_spotlight_content",
      dispatch,
    );
  }, []);

  useEffect(() => {
    if (!blogSpotlightContent?.length) return;
    const saved = blogSpotlightContent[0];
    setStatus(saved.status ?? false);
    const content = saved.content;

    if (Array.isArray(content?.cards)) {
      setCards(
        content.cards.map((c: any, i: number) => ({
          id: c.id || Math.random().toString(36).slice(2),
          colWidth: c.colWidth || 6,
          colWidthMobile: c.colWidthMobile ?? (i === 0 ? 12 : 6),
          label: c.label || "",
          title: c.title || "",
          description: c.description || "",
          image: c.image || "",
          linkType: c.linkType || "external",
          internalPostId: c.internalPostId || "",
          internalPostSlug: c.internalPostSlug || "",
          externalUrl: c.externalUrl || "",
          buttons: c.buttons || [],
          countdownLabel: c.countdownLabel || "",
          countdownDate: c.countdownDate || "",
        })),
      );
      return;
    }

    if (content?.mainCard) {
      setCards([
        {
          id: Math.random().toString(36).slice(2),
          colWidth: 6,
          colWidthMobile: 12,
          label: content.mainCard.category || "",
          title: content.mainCard.title || "",
          description: content.mainCard.description || "",
          image: content.mainCard.image || "",
          linkType: "external",
          internalPostId: "",
          internalPostSlug: "",
          externalUrl: content.mainCard.link || "",
          buttons: [],
          countdownLabel: "",
          countdownDate: "",
        },
        ...(content.secondaryCards || []).map(
          (sc: any): SpotlightCard => ({
            id: Math.random().toString(36).slice(2),
            colWidth: 3,
            colWidthMobile: 6,
            label: sc.category || "",
            title: sc.title || "",
            description: sc.description || "",
            image: sc.image || "",
            linkType: "external",
            internalPostId: "",
            internalPostSlug: "",
            externalUrl: sc.link || "",
            buttons: [],
            countdownLabel: "",
            countdownDate: "",
          }),
        ),
      ]);
    }
  }, [blogSpotlightContent]);

  const updateCard = useCallback(
    (id: string, updates: Partial<SpotlightCard>) => {
      setCards((prev) =>
        prev.map((c) => (c.id === id ? { ...c, ...updates } : c)),
      );
    },
    [],
  );
  const removeCard = useCallback(
    (id: string) => setCards((prev) => prev.filter((c) => c.id !== id)),
    [],
  );
  const moveCard = useCallback((id: string, dir: -1 | 1) => {
    setCards((prev) => {
      const idx = prev.findIndex((c) => c.id === id);
      const next = idx + dir;
      if (next < 0 || next >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[next]] = [arr[next], arr[idx]];
      return arr;
    });
  }, []);

  const totalCols = cards.reduce((sum, c) => sum + c.colWidth, 0);
  const colWarning = totalCols % 12 !== 0;

  const handleSave = async () => {
    setSaving(true);
    await create_faq_question(
      localStorage.getItem("token"),
      {
        name: "blog_spotlight_content",
        type: "blog_spotlight_content",
        status,
        content: { cards },
      },
      blogSpotlightContent?.[0]?._id,
      dispatch,
    );
    setSaving(false);
  };

  return (
    <div className="container-fluid" style={{ maxWidth: 960, paddingTop: 8 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 20,
        }}
      >
        <h3 className="fw-bold mb-0">Home Spotlight</h3>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button
            onClick={() => setShowPreview(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "7px 16px",
              border: "2px solid #000",
              borderRadius: 8,
              background: "#fff",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            <OpenInFull fontSize="small" /> Preview
          </button>
          <button
            onClick={handleSave}
            disabled={!isPermission || saving}
            style={{
              padding: "7px 20px",
              border: "2px solid #000",
              borderRadius: 8,
              background: "#FFD600",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: isPermission ? "pointer" : "default",
              opacity: isPermission ? 1 : 0.5,
            }}
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      <div
        style={{
          marginBottom: 16,
          padding: "10px 14px",
          background: "#f9f9f9",
          borderRadius: 8,
          border: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>
          <span style={{ fontWeight: 700, fontSize: "0.9rem" }}>
            Section visible on home page
          </span>
          <p style={{ margin: 0, fontSize: "0.78rem", color: "#888" }}>
            When off, the spotlight section is hidden from all users.
          </p>
        </div>
        <FormControlLabel
          control={
            <Switch
              checked={status}
              onChange={(e) => setStatus(e.target.checked)}
              disabled={!isPermission}
            />
          }
          label={status ? "On" : "Off"}
          labelPlacement="start"
        />
      </div>

      <div
        style={{
          marginBottom: 14,
          padding: "8px 14px",
          background: colWarning ? "#fff3e0" : "#e8f5e9",
          borderRadius: 8,
          border: `1px solid ${colWarning ? "#ffcc80" : "#a5d6a7"}`,
          fontSize: "0.8rem",
          fontWeight: 600,
          color: colWarning ? "#e65100" : "#2e7d32",
        }}
      >
        Desktop column total: <strong>{totalCols}</strong>/12
        {colWarning
          ? " — won't fill a complete row evenly."
          : " — fills one full row."}
      </div>

      {cards.map((card, i) => (
        <CardEditor
          key={card.id}
          card={card}
          index={i}
          total={cards.length}
          onUpdate={updateCard}
          onRemove={removeCard}
          onMove={moveCard}
          isPermission={isPermission}
        />
      ))}

      {cards.length < MAX_CARDS && isPermission && (
        <button
          onClick={() => setCards((prev) => [...prev, makeCard()])}
          style={{
            width: "100%",
            padding: "12px",
            border: "2px dashed #ccc",
            borderRadius: 10,
            background: "#fafafa",
            fontWeight: 700,
            fontSize: "0.85rem",
            cursor: "pointer",
            color: "#555",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            marginBottom: 20,
          }}
        >
          <Add /> Add Card ({cards.length}/{MAX_CARDS})
        </button>
      )}

      {showPreview && (
        <PreviewModal cards={cards} onClose={() => setShowPreview(false)} />
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  blogSpotlightContent: state.cms.blogSpotlightContent,
});
export default connect(mapStateToProps)(SpotlightManager);